import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2,
  Copy,
  Phone,
  Mail,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { candidatesApi } from '../../services/api';
import { format, parseISO } from 'date-fns';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso.slice(0, 10);
  }
};

const KeyTypeIcon = ({ type }) => {
  if (type === 'phone') return <Phone className="w-3.5 h-3.5" />;
  if (type === 'email') return <Mail className="w-3.5 h-3.5" />;
  return <UserIcon className="w-3.5 h-3.5" />;
};

const ClusterCard = ({ cluster, onMerge, busyId }) => {
  const [keepId, setKeepId] = useState(cluster.candidates[0]?.id || '');
  const [open, setOpen] = useState(true);
  const isBusy = busyId === cluster.key;

  const deleteIds = cluster.candidates
    .map((c) => c.id)
    .filter((id) => id !== keepId);

  return (
    <div
      className="rounded-xl border border-slate-200 overflow-hidden bg-white"
      data-testid={`dup-cluster-${cluster.key_type}-${cluster.key}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50/70 hover:bg-slate-100 transition"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide">
          <KeyTypeIcon type={cluster.key_type} />
          {cluster.key_type}
        </span>
        <span className="text-sm font-mono text-slate-700 truncate flex-1 text-left">
          {cluster.key}
        </span>
        <Badge
          variant="secondary"
          className="bg-rose-100 text-rose-700 border-rose-200"
          data-testid={`dup-cluster-count-${cluster.key}`}
        >
          {cluster.count} duplicates
        </Badge>
      </button>

      {open && (
        <div className="divide-y divide-slate-100">
          {cluster.candidates.map((c, idx) => {
            const isKeeper = c.id === keepId;
            return (
              <label
                key={c.id}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition ${
                  isKeeper ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                }`}
                data-testid={`dup-row-${c.id}`}
              >
                <input
                  type="radio"
                  name={`keep-${cluster.key}`}
                  value={c.id}
                  checked={isKeeper}
                  onChange={() => setKeepId(c.id)}
                  className="mt-1 accent-emerald-600"
                  data-testid={`dup-keep-radio-${c.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {c.name || 'Unnamed'}
                    </span>
                    {idx === 0 && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-px">
                        Earliest
                      </span>
                    )}
                    {isKeeper && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" /> Keep this
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email || '—'} · {c.phone || '—'}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {[c.current_designation, c.current_company, c.current_location]
                      .filter(Boolean)
                      .join(' · ') || 'No company / role info'}
                    {' · '}
                    Added {fmtDate(c.created_at)}
                    {c.source ? ` via ${c.source}` : ''}
                  </div>
                </div>
              </label>
            );
          })}

          <div className="px-4 py-3 bg-slate-50/50 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              <Trash2 className="w-3 h-3 inline mr-1" />
              {deleteIds.length} record{deleteIds.length === 1 ? '' : 's'} will be removed.
              Their pipeline applications and workbench notes will be re-pointed to the kept record.
            </div>
            <Button
              size="sm"
              onClick={() => onMerge(cluster, keepId, deleteIds)}
              disabled={isBusy || deleteIds.length === 0}
              className="bg-teal-700 hover:bg-teal-800 h-8"
              data-testid={`dup-merge-btn-${cluster.key}`}
            >
              {isBusy ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : (
                <Copy className="w-4 h-4 mr-1.5" />
              )}
              Merge {deleteIds.length} into kept
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const FindDuplicatesDialog = ({ isOpen, onClose, onMerged }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await candidatesApi.duplicatesReport(100);
      setReport(res.data);
    } catch (err) {
      toast.error('Failed to load duplicates report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchReport();
    else setReport(null);
  }, [isOpen, fetchReport]);

  const handleMerge = async (cluster, keepId, deleteIds) => {
    if (deleteIds.length === 0) return;
    setBusyKey(cluster.key);
    try {
      const res = await candidatesApi.mergeDuplicates(keepId, deleteIds);
      const r = res.data || {};
      toast.success(
        `Merged ${r.deleted_count} duplicate${r.deleted_count === 1 ? '' : 's'}` +
          (r.applications_repointed
            ? ` · ${r.applications_repointed} application${r.applications_repointed === 1 ? '' : 's'} re-pointed`
            : '')
      );
      // Refresh the report to remove this cluster
      await fetchReport();
      if (typeof onMerged === 'function') onMerged();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Merge failed';
      toast.error(msg);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col p-0"
        data-testid="find-duplicates-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-amber-600" />
            Find duplicate candidates
          </DialogTitle>
          <DialogDescription>
            Candidates are grouped by phone, then email, then fuzzy name. Pick which record to keep in each cluster — the rest are deleted and their pipeline + workbench history is re-pointed to the kept record.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2 space-y-3">
          {loading ? (
            <div
              className="flex items-center justify-center py-16 text-slate-500 text-sm"
              data-testid="find-duplicates-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Scanning your candidates database…
            </div>
          ) : !report ? null : report.cluster_count === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center"
              data-testid="find-duplicates-empty"
            >
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-3" />
              <h3 className="text-base font-semibold text-slate-900">
                No duplicates found
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                All {report.total_candidates} candidates in your database look unique.
              </p>
            </div>
          ) : (
            <>
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="find-duplicates-summary"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <div>
                  Found <strong>{report.cluster_count}</strong> duplicate cluster
                  {report.cluster_count === 1 ? '' : 's'} containing{' '}
                  <strong>{report.duplicate_records}</strong> redundant record
                  {report.duplicate_records === 1 ? '' : 's'} across{' '}
                  <strong>{report.total_candidates}</strong> total candidates. Review each cluster below — by default we suggest keeping the earliest record.
                </div>
              </div>

              {report.clusters.map((cl) => (
                <ClusterCard
                  key={`${cl.key_type}:${cl.key}`}
                  cluster={cl}
                  onMerge={handleMerge}
                  busyId={busyKey}
                />
              ))}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="text-xs text-slate-500">
            {report
              ? `Showing ${report.clusters?.length || 0} of ${report.cluster_count} clusters`
              : ''}
          </div>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            data-testid="find-duplicates-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FindDuplicatesDialog;
